function assertNever(x: never): never {
  throw new Error('Unexpected object: ' + x);
}

export enum Focus {
  /** Focus the first non-disabled item. */
  FIRST,
  /** Focus the previous non-disabled item. */
  PREVIOUS,
  /** Focus the next non-disabled item. */
  NEXT,
  /** Focus the last non-disabled item. */
  LAST,
  /** Focus a specific item based on the `id` of the item. */
  SPECIFIC,
  /** Focus no items at all. */
  NOTHING,
}

export function getIndex<MenuItem>(
  action:
    | { focus: Focus.SPECIFIC; id: string }
    | { focus: Exclude<Focus, Focus.SPECIFIC> },
  resolvers: {
    resolveItems(): MenuItem[];
    resolveActiveIndex(): number | null;
    resolveId(item: MenuItem): string;
    resolveDisabled(item: MenuItem): boolean;
  }
) {
  const items = resolvers.resolveItems();
  if (items.length <= 0) return null;

  const currentActiveIndex = resolvers.resolveActiveIndex();
  const activeIndex = currentActiveIndex ?? -1;

  const nextActiveIndex = (() => {
    switch (action.focus) {
      case Focus.FIRST:
        return items.findIndex((item) => !resolvers.resolveDisabled(item));

      case Focus.PREVIOUS: {
        const idx = items
          .slice()
          .reverse()
          .findIndex((item, idx, all) => {
            if (activeIndex !== -1 && all.length - idx - 1 >= activeIndex)
              return false;
            return !resolvers.resolveDisabled(item);
          });
        if (idx === -1) return idx;
        return items.length - 1 - idx;
      }

      case Focus.NEXT:
        return items.findIndex((item, idx) => {
          if (idx <= activeIndex) return false;
          return !resolvers.resolveDisabled(item);
        });

      case Focus.LAST: {
        const idx = items
          .slice()
          .reverse()
          .findIndex((item) => !resolvers.resolveDisabled(item));
        if (idx === -1) return idx;
        return items.length - 1 - idx;
      }

      case Focus.SPECIFIC:
        return items.findIndex(
          (item) => resolvers.resolveId(item) === action.id
        );

      case Focus.NOTHING:
        return null;

      default:
        assertNever(action);
    }
  })();

  return nextActiveIndex === -1 ? currentActiveIndex : nextActiveIndex;
}
